import { useState } from "react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

export default function Ontology() {
  const { data, error } = usePolling(() => api.get("/ontology/tree").then((r) => r.data), 15000);
  const [expanded, setExpanded] = useState({});

  if (error) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="ontology-error">Could not load ontology.</div>;
  if (!data) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="ontology-loading">Loading…</div>;

  const byParent = {};
  data.nodes.forEach((n) => {
    const key = n.parent_code || "root";
    byParent[key] = byParent[key] || [];
    byParent[key].push(n);
  });

  const critColor = { high: "#FF3B30", medium: "var(--oim-gold)", low: "var(--oim-green)" };

  const renderNode = (node, depth) => {
    const children = byParent[node.code] || [];
    const isOpen = expanded[node.code] !== false;
    const crit = node.node_type === "system" ? node.facets?.criticality : null;
    return (
      <div key={node.code} style={{ marginLeft: depth * 16 }}>
        <button
          className="flex items-center gap-2 py-1 text-xs hover:bg-[var(--oim-hover)] rounded px-1 w-full text-left"
          onClick={() => setExpanded((e) => ({ ...e, [node.code]: !isOpen }))}
          data-testid={`ontology-node-${node.code}`}
        >
          <span className="text-[9px] uppercase text-[var(--oim-text-dim)] w-20 shrink-0">{node.node_type}</span>
          <span className="text-[var(--oim-text-secondary)]">{node.name}</span>
          <span className="text-[9px] text-[var(--oim-text-dim)]">{node.chain}</span>
          {crit && (
            <span className="flex items-center gap-1.5 ml-2" data-testid={`ontology-criticality-${node.code}`}>
              {["safety", "production", "economic", "environmental"].map((dim) => (
                <span
                  key={dim}
                  className="text-[8px] font-heading uppercase"
                  style={{ color: critColor[crit[dim]] }}
                  title={`${dim}: ${crit[dim]}`}
                  data-testid={`ontology-criticality-${dim.slice(0, 2).toUpperCase()}`}
                >{dim.slice(0, 2)}</span>
              ))}
            </span>
          )}
        </button>
        {isOpen && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const roots = byParent["root"] || [];

  return (
    <div className="space-y-4" data-testid="ontology-page">
      <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Ontology</h1>
      <div className="text-xs text-[var(--oim-text-dim)]">Plant → ValueChain → Process → System → SubSystem → Element → Tag</div>
      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        {roots.map((r) => renderNode(r, 0))}
      </div>
    </div>
  );
}
